import { NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';

export type InlineSpinnerSize = 'sm' | 'md' | 'lg';
export type InlineSpinnerTone = 'current' | 'accent';

/** Diameter and stroke width in px for each size (UX spec: 16/20/48). */
const SPINNER_METRICS: Record<
  InlineSpinnerSize,
  { diameter: number; stroke: number }
> = {
  sm: { diameter: 16, stroke: 2 },
  md: { diameter: 20, stroke: 2.5 },
  lg: { diameter: 48, stroke: 4 },
};

/** Visible share of the ring (270 degrees). */
const ARC_RATIO = 0.75;

/**
 * Single loading indicator for the whole app: ring with a 270 degree arc over a
 * full track. `tone="current"` inherits the text color (use it inside buttons);
 * `tone="accent"` uses the dark orange accent (inputs and loose controls).
 *
 * The SVG is decorative (`aria-hidden`). The status text ("Buscando...",
 * "Guardando...") must be announced by the container with `role="status"`;
 * `label` only adds a visually hidden text for cases with no other announcement.
 */
@Component({
  selector: 'gd-inline-spinner',
  standalone: true,
  imports: [NgIf],
  templateUrl: './inline-spinner.component.html',
  styleUrls: ['./inline-spinner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InlineSpinnerComponent {
  @HostBinding('class.gd-inline-spinner') readonly hostClass = true;

  @HostBinding('class.gd-inline-spinner--sm') get isSm(): boolean {
    return this.size === 'sm';
  }

  @HostBinding('class.gd-inline-spinner--md') get isMd(): boolean {
    return this.size === 'md';
  }

  @HostBinding('class.gd-inline-spinner--lg') get isLg(): boolean {
    return this.size === 'lg';
  }

  @HostBinding('class.gd-inline-spinner--accent') get isAccent(): boolean {
    return this.tone === 'accent';
  }

  @HostBinding('class.gd-inline-spinner--current') get isCurrent(): boolean {
    return this.tone === 'current';
  }

  @Input() size: InlineSpinnerSize = 'md';
  @Input() tone: InlineSpinnerTone = 'current';
  /** Visually hidden text, read by assistive tech. */
  @Input() label: string | null = null;

  get diameter(): number {
    return SPINNER_METRICS[this.size].diameter;
  }

  get strokeWidth(): number {
    return SPINNER_METRICS[this.size].stroke;
  }

  get radius(): number {
    return (this.diameter - this.strokeWidth) / 2;
  }

  get center(): number {
    return this.diameter / 2;
  }

  get circumference(): number {
    return 2 * Math.PI * this.radius;
  }

  /** `stroke-dasharray`: visible arc length followed by the gap. */
  get arcDashArray(): string {
    const visible = this.circumference * ARC_RATIO;
    return `${visible} ${this.circumference - visible}`;
  }
}
